import { DateTimeHelper } from '@absg/angular-platform';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { TimePickerHour } from 'shared.models/time-picker-hour.model';
import { TimePickerMinute } from 'shared.models/time-picker-minute.model';
import { TimePickerPeriod } from 'shared.models/time-picker-period.model';

@Component({
  selector: '[nc-time-picker]',
  templateUrl: './time-picker.component.html',
  standalone: false,
})
export class TimePickerComponent implements OnInit, OnDestroy {
  @Input() isModalOpen: Observable<boolean>;
  @Input() selectedTime: Observable<string>;

  @Output() cancelModal = new EventEmitter<null>();
  @Output() enterModal = new EventEmitter<string>();

  hourRows: TimePickerHour[][];
  minuteRows: TimePickerMinute[][];
  periodRows: TimePickerPeriod[];
  selectedTimeSubscription: Subscription;
  selectedHour: TimePickerHour = null;
  selectedMinute: TimePickerMinute = null;
  selectedPeriod: TimePickerPeriod = null;

  constructor() { }

  ngOnInit(): void {
    this.initializeHours();
    this.initializeMinutes();
    this.initializePeriods();

    this.selectedTimeSubscription = this.selectedTime.subscribe((selectedTimeValue: string) => {
      this.initializeSelectedTime(selectedTimeValue);
    });
  }

  ngOnDestroy(): void {
    if (this.selectedTimeSubscription) {
      this.selectedTimeSubscription.unsubscribe();
    }
  }

  initializeHours(): void {
    const hourValueRows: number[][] = [
      [1, 2, 3, 4],
      [5, 6, 7, 8],
      [9, 10, 11, 12],
    ];

    const hourRows: TimePickerHour[][] = [];

    hourValueRows.forEach((hourValueRow: number[]) => {
      const hourRow: TimePickerHour[] = [];

      hourValueRow.forEach((hourValue: number) => {
        const hour: TimePickerHour = this.createTimePickerHour(hourValue);
        hourRow.push(hour);
      });

      hourRows.push(hourRow);
    });

    this.hourRows = hourRows;
  }

  initializeMinutes(): void {
    const minuteValueRows: number[][] = [
      [0, 15],
      [30, 45],
    ];

    const minuteRows: TimePickerMinute[][] = [];

    minuteValueRows.forEach((minuteValueRow: number[]) => {
      const minuteRow: TimePickerMinute[] = [];

      minuteValueRow.forEach((minuteValue: number) => {
        const minute: TimePickerMinute = this.createTimePickerMinute(minuteValue);
        minuteRow.push(minute);
      });

      minuteRows.push(minuteRow);
    });

    this.minuteRows = minuteRows;
  }

  initializePeriods(): void {
    const periods: string[] = DateTimeHelper.getPeriods();
    const periodRows: TimePickerPeriod[] = [];

    periods.forEach((period: string) => {
      const periodRow = new TimePickerPeriod(period);
      periodRows.push(periodRow);
    });

    this.periodRows = periodRows;
  }

  initializeSelectedTime(selectedTimeValue: string): void {
    if (selectedTimeValue) {
      const selectedHourValue: number = DateTimeHelper.getHourByTwelveHourFormat(selectedTimeValue);
      this.selectedHour = this.createTimePickerHour(selectedHourValue);

      const selectedMinuteValue: number = DateTimeHelper.getMinute(selectedTimeValue);
      this.selectedMinute = this.createTimePickerMinute(selectedMinuteValue);

      const selectedPeriodValue = DateTimeHelper.getPeriod(selectedTimeValue);
      this.selectedPeriod = new TimePickerPeriod(selectedPeriodValue);
    } else {
      this.selectedHour = null;
      this.selectedMinute = null;
      this.selectedPeriod = null;
    }
  }

  onCancelButtonClicked(event: Event): void {
    event.preventDefault();

    this.cancelModal.emit();
  }

  onSaveButtonClicked(event: Event): void {
    event.preventDefault();

    const selectedTime: string = this.getSelectedTime();

    this.enterModal.emit(selectedTime);
  }

  createTimePickerHour(hour: number): TimePickerHour {
    return new TimePickerHour(hour, DateTimeHelper.formatDateTimeComponent(hour));
  }

  createTimePickerMinute(minute: number): TimePickerMinute {
    return new TimePickerMinute(minute, DateTimeHelper.formatDateTimeComponent(minute));
  }

  getSelectedTime(): string {
    let selectedTime: string = null;

    if (this.hasSelectedHour() && this.hasSelectedMinute() && this.hasSelectedPeriod()) {
      selectedTime = DateTimeHelper.formatTimeByComponents(
        this.selectedHour.value, this.selectedMinute.value, this.selectedPeriod.value);
    }

    return selectedTime;
  }

  setSelectedHour(hour: TimePickerHour): void {
    this.selectedHour = hour;
  }

  setSelectedMinute(minute: TimePickerMinute): void {
    this.selectedMinute = minute;
  }

  setSelectedPeriod(period: TimePickerPeriod): void {
    this.selectedPeriod = period;
  }

  hasSelectedHour(): boolean {
    return this.selectedHour !== null;
  }

  isSelectedHour(hour: TimePickerHour): boolean {
    const isSelectedHour = this.hasSelectedHour() && (hour.value === this.selectedHour.value);

    return isSelectedHour;
  }

  hasSelectedMinute(): boolean {
    return this.selectedMinute !== null;
  }

  isSelectedMinute(minute: TimePickerMinute): boolean {
    const isSelectedMinute = this.hasSelectedMinute() && (minute.value === this.selectedMinute.value);

    return isSelectedMinute;
  }

  hasSelectedPeriod(): boolean {
    return this.selectedPeriod !== null;
  }

  isSelectedPeriod(period: TimePickerPeriod): boolean {
    const isSelectedPeriod = this.hasSelectedPeriod() && (period.value === this.selectedPeriod.value);

    return isSelectedPeriod;
  }

  trackItem(index: number, __: unknown): number {
    return index;
  }
}
